import React, { useEffect } from 'react';
import { Route, Switch, useLocation, matchPath, useHistory } from 'react-router';

// Types
import { Feature } from 'Shared/types/Organization.type';

// Hooks
import { useFeature } from 'Shared/utils/hooks';
import { useOrganization } from 'Shared/utils/hooks/use-organization';
import { useSmac } from 'Shared/utils/hooks/use-smac';
import { useTooltip } from 'Wpds/components/tooltip/useTooltip';

// Constants
import PageUrlsConstants from 'Shared/constants/PageUrls.constants';
import { ESSENTIALS_TOOLTIP } from 'Shared/constants/PerformEssentials.constants';

// Components
import { WpPageLayout, WpPageHeader, WpPageTitle, WpPageBody } from 'Wpds/components/page-layout';
import { WpTabs, WpTab } from 'Wpds/components/tabs';
import { WpIcon } from 'Wpds/components/icon';
import CoachingFeedbackPage from './CoachingFeedbackPage/CoachingFeedbackPage';
import CoachingKudosPage from './CoachingKudosPage/CoachingKudosPage';
import CoachingReviewsPage from './CoachingReviewsPage/CoachingReviewsPage';
import WpTooltipOverlay from 'Wpds/components/tooltip/WpTooltipOverlay';

// Styles
import './CoachingPage.scss';

const CoachingPage = () => {
  const { pathname } = useLocation();

  const { isFreePlan, isFreeTrial } = useFeature(Feature.reviews);
  const { isActive: isKudosActive } = useFeature(Feature.kudos);
  const { isPiDemoAccount } = useOrganization();
  const { isPartnerAccount } = useSmac();
  const tooltip = useTooltip();

  const isLocked = isFreePlan || isFreeTrial || isPiDemoAccount || isPartnerAccount;

  const history = useHistory();

  useEffect(() => {
    // this handles the old route (/coaching) to redirect to the first/ default tab (/feedback/kudos)
    // presereve the old route for the old coahing page
    if (matchPath(pathname, { path: PageUrlsConstants.COACHING, exact: true })) {
      if (isKudosActive) {
        history.push(PageUrlsConstants.COACHING_KUDOS);
      } else {
        history.push(PageUrlsConstants.COACHING_FEEDBACK);
      }
    }
  }, [pathname]);

  const baseClassName = 'CoachingPage';
  return (
    <>
      <WpPageLayout className={baseClassName}>
        <WpPageHeader slim sticky>
          <WpPageTitle title="Feedback" />
          <WpTabs>
            {isKudosActive && <WpTab to={PageUrlsConstants.COACHING_KUDOS}>Kudos</WpTab>}
            <WpTab to={PageUrlsConstants.COACHING_FEEDBACK}>1:1 Feedback</WpTab>
            <WpTab to={PageUrlsConstants.COACHING_360} ref={tooltip.targetRef}>
              360 Feedback
              {isLocked && (
                <span className={`${baseClassName}__tabs__tab__locked-icon`}>
                  <WpIcon name="lock-alt" />
                </span>
              )}
            </WpTab>
          </WpTabs>
        </WpPageHeader>
        <WpPageBody>
          <Switch>
            {isKudosActive && <Route path={PageUrlsConstants.COACHING_KUDOS} component={CoachingKudosPage} exact />}
            <Route path={PageUrlsConstants.COACHING_FEEDBACK} component={CoachingFeedbackPage} />
            <Route path={PageUrlsConstants.COACHING_360} component={CoachingReviewsPage} />
          </Switch>
        </WpPageBody>
      </WpPageLayout>
      {isLocked && <WpTooltipOverlay context={tooltip}>{ESSENTIALS_TOOLTIP}</WpTooltipOverlay>}
    </>
  );
};

export default CoachingPage;
